// Babble voices (decision 0045: "no voice acting; NPC lines as text with a short babble voice pitched per character").
// A voice is data (pitch, formant scale, syllable rate, breath, roughness); a line becomes a plan of syllables (one
// per couple of letters, at the voice's rate, fitted to the line's seconds when it has them), each a vowel from a small
// set, with a pitch contour that falls at a full stop and rises at a question mark. The syllables are rendered once per
// voice and vowel (a glottal pulse train through three formant band-passes) and replayed at the plan's pitch.
import { dcBlock, fades, fnv1a, mulberry32, normalise, Svf, variantRng } from "./dsp";

/** A character's babble voice. */
export interface BabbleVoice {
  /** The voice's pitch (Hz): about 90 to 140 deep, 180 to 260 light, 280 to 360 a child. */
  pitch: number;
  /** Formant scale: 0.85 a large voice, 1 average, 1.2 a small one. */
  formant: number;
  /** Syllables per second. */
  rate: number;
  /** Breath noise mixed in (0..1). */
  breath: number;
  /** Roughness (0..1): period jitter and a growl. */
  rough: number;
}

/** The vowels a syllable can take, with their first three formants (Hz, an average adult). */
export const VOWELS: Record<string, [number, number, number]> = {
  a: [730, 1090, 2440],
  e: [530, 1840, 2480],
  i: [300, 2200, 2900],
  o: [570, 840, 2410],
  u: [440, 1020, 2240],
};
const VOWEL_IDS = Object.keys(VOWELS);

/** One syllable of a plan. */
export interface Syllable {
  /** Seconds from the line's start. */
  at: number;
  vowel: string;
  /** Pitch as a playback rate (1 = the voice's own). */
  rate: number;
  /** Gain (linear). */
  gain: number;
}

/** A line's babble: its syllables and how long it lasts. */
export interface BabblePlan {
  syllables: Syllable[];
  seconds: number;
}

/** Plan a line's syllables. Seeded by the line's text, so a line always babbles the same. */
export function babblePlan(text: string, voice: BabbleVoice, seconds?: number): BabblePlan {
  const letters = text.replace(/[^\p{L}\p{N}]/gu, "").length;
  const count = Math.max(1, Math.min(48, Math.round(letters / 2.6)));
  const natural = count / voice.rate;
  // a line shown for `seconds` babbles for at most most of it (the text is still there after the voice stops)
  const total = seconds ? Math.min(natural, Math.max(0.3, seconds * 0.85)) : natural;
  const gap = total / count;
  const rng = mulberry32(fnv1a(text));
  const question = /\?\s*$/.test(text);
  const out: Syllable[] = [];
  for (let i = 0; i < count; i++) {
    const u = count > 1 ? i / (count - 1) : 1;
    // a gentle declination, a fall or rise on the last two syllables, a little jitter
    let contour = 1.06 - 0.1 * u;
    if (i >= count - 2) contour *= question ? 1.18 : 0.9;
    const jitter = Math.pow(2, ((rng() - 0.5) * 2.4) / 12);
    out.push({ at: i * gap + (rng() - 0.5) * gap * 0.15, vowel: VOWEL_IDS[Math.floor(rng() * VOWEL_IDS.length)]!, rate: contour * jitter, gain: 0.75 + 0.25 * rng() });
  }
  if (out[0]) out[0].at = 0;
  return { syllables: out, seconds: total };
}

/** Render one syllable of a voice (mono, at `sr`): a glottal pulse train through the vowel's formants, an envelope of
 *  `length` seconds. Pure; the engine caches it per voice and vowel. */
export function renderSyllable(id: string, voice: BabbleVoice, vowel: string, sr = 22050, length = 0.14): Float32Array {
  const n = Math.round(length * sr);
  const out = new Float32Array(n);
  const rng = variantRng(`${id}:${vowel}`, 0);
  const f = VOWELS[vowel] ?? VOWELS.a!;
  const filters = f.map((fc, k) => {
    const s = new Svf(sr);
    s.set(Math.min(fc * voice.formant, sr * 0.45), 6 + k * 2);
    return s;
  });
  const gains = [1, 0.6, 0.3];
  let phase = 0;
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    // a slight pitch fall across the syllable, and jitter for roughness
    const f0 = voice.pitch * (1.04 - 0.08 * (t / length)) * (1 + (rng() - 0.5) * 0.04 * voice.rough);
    phase += f0 / sr;
    let pulse = 0;
    if (phase >= 1) {
      phase -= 1;
      pulse = 1;
    }
    // the glottal source: pulses, softened, plus breath; roughness adds a subharmonic growl
    const src = pulse * 2 + (rng() * 2 - 1) * voice.breath * 0.35 + (voice.rough > 0 ? Math.sin(Math.PI * 2 * f0 * 0.5 * t) * 0.08 * voice.rough : 0);
    let y = 0;
    for (let k = 0; k < 3; k++) y += filters[k]!.bp(src) * gains[k]!;
    const a = 0.02;
    const e = t < a ? t / a : Math.exp(-(t - a) / (length * 0.45));
    out[i] = y * e;
  }
  dcBlock(out, sr);
  fades(out, sr, 0.002, 0.02);
  normalise([out], sr, "short", -18);
  return out;
}
